import { type VariantProps, cva } from "class-variance-authority";
import React, { useMemo } from "react";
import type { HTMLAttributes } from "react";

import { cn } from "~/lib/cn";

export const cardVariants = cva(
  "bg-card p-6 flex flex-col gap-4 text-card-foreground rounded-2xl transition",
  {
    variants: {
      variant: {
        default: "border border-gray-100 shadow-sm",
        bordered: "border border-gray-100 shadow-sm",
        /** A card that is itself a link or button: it answers the pointer. */
        interactive:
          "border border-gray-100 shadow-sm hover:border-purple-200 hover:shadow-md",
        flat: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type CardVariantProps = VariantProps<typeof cardVariants>;

/** The standard box that groups related content, with Header, Title, Content and Footer parts. */
export const Card = React.memo<
  HTMLAttributes<HTMLDivElement> & CardVariantProps & { error?: unknown }
>(({ className, variant, error, children, ...props }) => {
  const cardClassName = useMemo(
    () =>
      cn(
        cardVariants({ variant }),
        {
          "border border-red-700": error,
        },
        className,
      ),
    [variant, error, className],
  );

  return (
    <div className={cardClassName} {...props}>
      {children}
    </div>
  );
});

Card.displayName = "Card";

export const CardHeader = React.memo<HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }) => {
    const headerClassName = useMemo(
      () => cn("flex flex-col gap-1", className),
      [className],
    );

    return (
      <div className={headerClassName} {...props}>
        {children}
      </div>
    );
  },
);

CardHeader.displayName = "CardHeader";

export const CardTitle = React.memo<
  HTMLAttributes<HTMLHeadingElement> & { as?: "h2" | "h3" }
>(({ as: Tag = "h3", className, children, ...props }) => {
  const titleClassName = useMemo(
    () =>
      cn(
        "font-heading text-lg leading-snug font-medium text-gray-900",
        className,
      ),
    [className],
  );

  return (
    <Tag className={titleClassName} {...props}>
      {children}
    </Tag>
  );
});

CardTitle.displayName = "CardTitle";

export const CardDescription = React.memo<HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }) => {
    const descriptionClassName = useMemo(
      () => cn("text-sm text-muted-foreground", className),
      [className],
    );

    return (
      <p className={descriptionClassName} {...props}>
        {children}
      </p>
    );
  },
);

CardDescription.displayName = "CardDescription";

export const CardContent = React.memo<HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }) => {
    const contentClassName = useMemo(() => cn("", className), [className]);

    return (
      <div className={contentClassName} {...props}>
        {children}
      </div>
    );
  },
);

CardContent.displayName = "CardContent";

export const CardFooter = React.memo<HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }) => {
    const footerClassName = useMemo(
      () => cn("flex items-center pt-4", className),
      [className],
    );

    return (
      <div className={footerClassName} {...props}>
        {children}
      </div>
    );
  },
);

CardFooter.displayName = "CardFooter";
